import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { API_URL } from '../config';
import { getSession, session } from '../api/session';
import { type ClientToServerEvents, type ServerToClientEvents, SOCKET_NAMESPACE } from './socket-events';

export type EventsSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/** Wait before trying again after the server refused the token twice in a row. */
const REFUSED_RETRY_MS = 30_000;

function createSocket(): EventsSocket {
  return io(`${API_URL}${SOCKET_NAMESPACE}`, {
    // Read on every (re)connect: after a token refresh or a network drop it uses the current token.
    auth: (send) => send({ token: getSession().accessToken }),
    autoConnect: false,
    transports: ['websocket'],
    withCredentials: true,
  });
}

/**
 * One WebSocket to the backend, connected while `enabled` (signed in).
 * Socket.IO reconnects by itself after a network drop, but not when the server says no: when the
 * token is refused or the server closes the connection because the token expired. Both are handled
 * here, so live updates never stop for good.
 */
export function useEventsSocket(enabled: boolean): { socket: EventsSocket | null; connected: boolean } {
  const [socket] = useState(createSocket);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let refusedInARow = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const reconnect = async (renewToken: boolean) => {
      if (renewToken) await session.refresh();
      if (!stopped && getSession().accessToken) socket.connect();
    };
    const up = () => {
      refusedInARow = 0;
      setConnected(true);
    };
    const down = (reason: Socket.DisconnectReason) => {
      setConnected(false);
      // The server closed it: the access token expired. Reconnect with the current one.
      if (reason === 'io server disconnect') void reconnect(false);
    };
    const refused = (error: Error) => {
      setConnected(false);
      if (error.message !== 'unauthorized') return; // network trouble: Socket.IO retries by itself
      refusedInARow += 1;
      if (refusedInARow === 1) void reconnect(true);
      else retryTimer = setTimeout(() => void reconnect(true), REFUSED_RETRY_MS);
    };

    socket.on('connect', up);
    socket.on('disconnect', down);
    socket.on('connect_error', refused);
    socket.connect();
    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      socket.disconnect();
      socket.off('connect', up);
      socket.off('disconnect', down);
      socket.off('connect_error', refused);
    };
  }, [enabled, socket]);

  return { socket: enabled ? socket : null, connected: enabled && connected };
}
